import { useEffect, useRef } from "react";
import "../../customCSS/ClockLoader.css";


interface ClockLoaderProps {
  /** Diameter of the clock in pixels. Controls everything proportionally. */
  size?: number;
  /** Clock face background colour */
  faceColor?: string;
  /** Outer ring / glow colour */
  ringColor?: string;
  /** Hour hand colour */
  hourHandColor?: string;
  /** Minute hand colour */
  minuteHandColor?: string;
  /** Centre cap colour */
  centerColor?: string;
  /** Tick mark colour */
  tickColor?: string;
  /** Seconds for one full minute-hand revolution (hour hand is 12× slower) */
  minuteSpeed?: number;
  /** Show "Loading …" label underneath */
  showLabel?: boolean;
  /** Label text */
  label?: string;
  /** Label colour */
  labelColor?: string;
  /** Extra className on the wrapper */
  className?: string;
}

export function ClockLoader({
  size = 160,
  faceColor = "var(--theme-bg-secondary, #1e1b2e)",
  ringColor = "var(--theme-accent, #6366f1)",
  hourHandColor = "var(--theme-accent-dark, #c4b5fd)",
  minuteHandColor = "var(--theme-text-primary, #ffffff)",
  centerColor = "var(--theme-accent, #7c3aed)",
  tickColor = "var(--theme-accent-light, #a78bfa)",
  minuteSpeed = 4,
  showLabel = true,
  label = "Loading",
  labelColor = "var(--gold-500, #a78bfa)",
  className = "",
}: ClockLoaderProps) {
  const minuteRef = useRef<SVGLineElement>(null);
  const hourRef = useRef<SVGLineElement>(null);
  const startRef = useRef(performance.now());
  const rafRef = useRef(0);

  const hourSpeed = minuteSpeed * 12; // hour hand is 12× slower

  useEffect(() => {
    function tick() {
      const elapsed = (performance.now() - startRef.current) / 1000; // seconds

      // Degrees of clockwise rotation
      const minuteDeg = (elapsed / minuteSpeed) * 360;
      const hourDeg = (elapsed / hourSpeed) * 360;

      // Convert degrees → SVG endpoint, 12 o'clock = -90° offset
      const toXY = (deg: number, length: number) => {
        const rad = ((deg - 90) * Math.PI) / 180;
        return { x: 100 + Math.cos(rad) * length, y: 100 + Math.sin(rad) * length };
      };

      const min = toXY(minuteDeg, 60);
      const hr = toXY(hourDeg, 40);

      minuteRef.current?.setAttribute("x2", String(min.x));
      minuteRef.current?.setAttribute("y2", String(min.y));
      hourRef.current?.setAttribute("x2", String(hr.x));
      hourRef.current?.setAttribute("y2", String(hr.y));

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [minuteSpeed, hourSpeed]);

  const cx = 100, cy = 100;

  // Hour tick marks (12 positions)
  const hourTicks = Array.from({ length: 12 }, (_, i) => {
    const angle = ((i * 30 - 90) * Math.PI) / 180;
    const isBig = i % 3 === 0;
    return {
      key: i,
      x1: cx + Math.cos(angle) * (isBig ? 80 : 83),
      y1: cy + Math.sin(angle) * (isBig ? 80 : 83),
      x2: cx + Math.cos(angle) * 91,
      y2: cy + Math.sin(angle) * 91,
      sw: isBig ? 2.5 : 1.5,
      op: isBig ? 0.9 : 0.5,
    };
  });

  // Minor tick marks (60 positions, skip multiples of 5)
  const minorTicks = Array.from({ length: 60 }, (_, i) => {
    if (i % 5 === 0) return null;
    const angle = ((i * 6 - 90) * Math.PI) / 180;
    return {
      key: i,
      x1: cx + Math.cos(angle) * 87,
      y1: cy + Math.sin(angle) * 87,
      x2: cx + Math.cos(angle) * 91,
      y2: cy + Math.sin(angle) * 91,
    };
  }).filter(Boolean) as { key: number; x1: number; y1: number; x2: number; y2: number }[];

  return (
    <div className={`clock-loader-wrapper ${className}`}>
      {/* Pulsing glow halo */}
      <div
        className="clock-loader-glow"
        style={{ width: size, height: size, "--glow-color": ringColor } as React.CSSProperties}
      />

      <svg
        className="clock-loader-svg"
        width={size}
        height={size}
        viewBox="0 0 200 200"
      >
        <defs>
          <filter id="cl-hand-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="cl-dot-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Face */}
        <circle cx={cx} cy={cy} r="92" fill={faceColor} />

        {/* Outer ring */}
        <circle cx={cx} cy={cy} r="92" fill="none" stroke={ringColor} strokeWidth="2" strokeOpacity="0.8" />

        {/* Inner accent ring */}
        <circle cx={cx} cy={cy} r="78" fill="none" stroke={ringColor} strokeWidth="0.5" strokeOpacity="0.2" />

        {/* Minor ticks */}
        {minorTicks.map((t) => (
          <line
            key={t.key}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={tickColor} strokeWidth="0.8" strokeLinecap="round" strokeOpacity="0.3"
          />
        ))}

        {/* Hour ticks */}
        {hourTicks.map((t) => (
          <line
            key={t.key}
            x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={tickColor} strokeWidth={t.sw} strokeLinecap="round" strokeOpacity={t.op}
          />
        ))}

        {/* Hour hand — imperatively moved each frame via ref */}
        <line
          ref={hourRef}
          x1={cx} y1={cy}
          x2={cx} y2={cy - 40}
          stroke={hourHandColor}
          strokeWidth="5"
          strokeLinecap="round"
          filter="url(#cl-hand-glow)"
        />

        {/* Minute hand */}
        <line
          ref={minuteRef}
          x1={cx} y1={cy}
          x2={cx} y2={cy - 60}
          stroke={minuteHandColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          filter="url(#cl-hand-glow)"
        />

        {/* Centre cap */}
        <circle cx={cx} cy={cy} r="5.5" fill={centerColor} filter="url(#cl-dot-glow)" />
        <circle cx={cx} cy={cy} r="2.5" fill="#ffffff" fillOpacity="0.9" />
      </svg>

      {showLabel && (
        <div className="clock-loader-label" style={{ color: labelColor }}>
          {label}
          <span className="clock-loader-dots">
            <span /><span /><span />
          </span>
        </div>
      )}
    </div>
  );
}